import React, { useState, useEffect } from 'react';
import { inboxFetch } from '../lib/inboxApi';
import { colors } from '../theme/tokens';

const STATUS_COLORS = {
  open: '#ef4444',
  pending: '#f59e0b',
  resolved: '#10b981'
};

export default function InboxAnalytics() {
  const [period, setPeriod] = useState('week');
  const [overview, setOverview] = useState(null);
  const [perAgent, setPerAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ovRes, paRes] = await Promise.all([
        inboxFetch(`/api/analytics?type=overview&period=${period}`),
        inboxFetch(`/api/analytics?type=per-agent&period=${period}`)
      ]);
      if (!ovRes.ok || !paRes.ok) throw new Error('Server error');
      setOverview(await ovRes.json());
      setPerAgent(await paRes.json());
    } catch (err) {
      setError('Could not load analytics. Check your inbox server connection.');
    } finally {
      setLoading(false);
    }
  };

  const periodLabel = { day: 'Today', week: 'This Week', month: 'This Month' };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>Analytics</h2>
        <div style={s.periodToggle}>
          {['day', 'week', 'month'].map(p => (
            <button
              key={p}
              style={{ ...s.periodBtn, ...(period === p ? s.periodBtnActive : {}) }}
              onClick={() => setPeriod(p)}
            >
              {p === 'day' ? 'Day' : p === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
      </div>

      {loading && <div style={s.loading}>Loading analytics…</div>}
      {error && <div style={s.error}>{error}</div>}

      {!loading && overview && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Conversations — {periodLabel[period]}</div>
          <div style={s.cards}>
            {['open', 'pending', 'resolved'].map(status => {
              const item = overview.find(o => o._id === status);
              return (
                <div key={status} style={{ ...s.card, borderTop: `4px solid ${STATUS_COLORS[status]}` }}>
                  <div style={s.cardCount}>{item ? item.count : 0}</div>
                  <div style={s.cardLabel}>{status.charAt(0).toUpperCase() + status.slice(1)}</div>
                </div>
              );
            })}
            <div style={{ ...s.card, borderTop: '4px solid #6366f1' }}>
              <div style={s.cardCount}>{overview.reduce((sum, o) => sum + o.count, 0)}</div>
              <div style={s.cardLabel}>Total</div>
            </div>
          </div>
        </div>
      )}

      {!loading && perAgent && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Per Agent — {periodLabel[period]}</div>
          {perAgent.length === 0 ? (
            <div style={s.empty}>No agent activity in this period</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Agent</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Messages Sent</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Resolved</th>
                </tr>
              </thead>
              <tbody>
                {perAgent.map(agent => (
                  <tr key={agent.agent} style={s.tr}>
                    <td style={s.td}>{agent.agent}</td>
                    <td style={{ ...s.td, textAlign: 'center', fontWeight: '600', color: '#2fb65c' }}>
                      {agent.messagesSent}
                    </td>
                    <td style={{ ...s.td, textAlign: 'center', fontWeight: '600', color: '#10b981' }}>
                      {agent.resolved}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  container: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: colors.text
  },
  periodToggle: {
    display: 'flex',
    gap: '6px'
  },
  periodBtn: {
    padding: '6px 16px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '20px',
    background: colors.surfaceDeep,
    color: colors.textMuted,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  },
  periodBtnActive: {
    background: 'rgba(47,182,92,0.20)',
    borderColor: 'rgba(47,182,92,0.55)',
    color: colors.parGreenBright
  },
  loading: {
    textAlign: 'center',
    color: colors.textMuted,
    padding: '40px',
    fontSize: '14px'
  },
  error: {
    background: 'rgba(229,84,75,0.14)',
    color: '#f3b0aa',
    border: '1px solid rgba(229,84,75,0.35)',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px'
  },
  section: {
    marginBottom: '32px'
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '12px'
  },
  cards: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap'
  },
  card: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '12px',
    padding: '16px 20px',
    minWidth: '110px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
  },
  cardCount: {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text,
    lineHeight: 1
  },
  cardLabel: {
    fontSize: '12px',
    color: colors.textMuted,
    marginTop: '4px',
    fontWeight: '600'
  },
  empty: {
    color: colors.textMuted,
    fontSize: '13px',
    padding: '20px 0'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
  },
  th: {
    padding: '10px 16px',
    background: colors.surfaceSoft,
    fontSize: '12px',
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: `1px solid ${colors.glassBorder}`,
    textAlign: 'left'
  },
  tr: {
    borderBottom: `1px solid ${colors.borderSoft}`
  },
  td: {
    padding: '10px 16px',
    fontSize: '13px',
    color: colors.text
  }
};
